import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { useLeague } from 'contexts/LeagueContext';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';

const LeagueHub = () => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { leagues, loading, leaguesError, createLeague, joinLeague, switchLeague, archiveLeague, unarchiveLeague, deleteLeague, restoreLeague, purgeLeague } = useLeague();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [leagueName, setLeagueName] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [createdLeague, setCreatedLeague] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // league object
  const [deleteNameInput, setDeleteNameInput] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [actionError, setActionError] = useState(null);

  const handleCreate = async (e) => {
    e?.preventDefault();
    if (!leagueName.trim()) return;
    setError(null);
    setSubmitting(true);
    try {
      const league = await createLeague(leagueName.trim());
      setCreatedLeague(league);
      setLeagueName('');
    } catch (err) {
      setError(err?.message || 'Failed to create league');
    } finally {
      setSubmitting(false);
    }
  };

  const handleJoin = async (e) => {
    e?.preventDefault();
    if (!joinCode.trim()) return;
    setError(null);
    setSubmitting(true);
    try {
      const league = await joinLeague(joinCode.trim());
      setShowJoinModal(false);
      setJoinCode('');
      switchLeague(league.id);
      navigate('/');
    } catch (err) {
      setError(err?.message || 'Failed to join league');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSelectLeague = (leagueId) => {
    switchLeague(leagueId);
    navigate('/');
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch {}
  };

  const handleCopyCode = async (code) => {
    try {
      await navigator.clipboard?.writeText(code);
    } catch {}
  };

  const handleArchive = async (league) => {
    setActionError(null);
    try {
      await archiveLeague(league.id);
    } catch (err) {
      setActionError(err?.message || 'Failed to archive league');
    }
  };

  const handleUnarchive = async (league) => {
    setActionError(null);
    try {
      await unarchiveLeague(league.id);
    } catch (err) {
      setActionError(err?.message || 'Failed to unarchive league');
    }
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;
    if (deleteNameInput.trim() !== confirmDelete.name) return;
    setDeleting(true);
    setActionError(null);
    try {
      await deleteLeague(confirmDelete.id);
      setConfirmDelete(null);
      setDeleteNameInput('');
    } catch (err) {
      setActionError(err?.message || 'Failed to delete league');
    } finally {
      setDeleting(false);
    }
  };

  const handleRestore = async (league) => {
    setActionError(null);
    try {
      await restoreLeague(league.id);
    } catch (err) {
      setActionError(err?.message || 'Failed to restore league');
    }
  };

  const handlePurge = async (league) => {
    setActionError(null);
    try {
      await purgeLeague(league.id);
    } catch (err) {
      setActionError(err?.message || 'Failed to purge league');
    }
  };

  // Sort active leagues alphabetically; deleted/archived sections sort the same way.
  const sortByName = (a, b) => a.name.localeCompare(b.name);
  const activeLeagues = leagues.filter((l) => !l.archived_at && !l.deleted_at).sort(sortByName);
  const archivedLeagues = leagues.filter((l) => l.archived_at && !l.deleted_at).sort(sortByName);
  // Pending-deletion leagues are only shown to the owner — members shouldn't worry about it.
  const pendingDeleteLeagues = leagues.filter((l) => l.deleted_at && l.role === 'owner').sort(sortByName);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--color-background)' }}>
        <div className="w-8 h-8 border-4 rounded-full animate-spin" style={{ borderColor: 'var(--color-border)', borderTopColor: 'var(--color-primary)' }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4" style={{ backgroundColor: 'var(--color-background)' }}>
      <div className="max-w-lg mx-auto pt-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'var(--color-primary)' }}>
              <span style={{ fontSize: '20px', lineHeight: 1 }}>🏀</span>
            </div>
            <div>
              <span className="font-heading font-bold text-lg block" style={{ color: 'var(--color-foreground)' }}>
                Hoop<span style={{ color: 'var(--color-primary)' }}>Stats</span>
              </span>
              <span className="font-caption text-xs" style={{ color: 'var(--color-muted-foreground)' }}>
                {user?.email}
              </span>
            </div>
          </div>
          <button
            onClick={handleSignOut}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-caption text-xs font-medium transition-all hover:opacity-80"
            style={{ backgroundColor: 'var(--color-muted)', color: 'var(--color-muted-foreground)' }}
          >
            <Icon name="LogOut" size={13} color="currentColor" />
            Sign Out
          </button>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-2 gap-3 mb-8">
          <button
            onClick={() => { setShowCreateModal(true); setError(null); setCreatedLeague(null); }}
            className="flex flex-col items-center gap-2 p-5 rounded-xl border-2 border-dashed transition-all hover:border-[var(--color-primary)] hover:bg-[var(--color-primary)]/5"
            style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-card)' }}
          >
            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(232,93,4,0.12)' }}>
              <Icon name="Plus" size={20} color="var(--color-primary)" />
            </div>
            <span className="font-caption text-sm font-semibold" style={{ color: 'var(--color-foreground)' }}>Create League</span>
          </button>
          <button
            onClick={() => { setShowJoinModal(true); setError(null); }}
            className="flex flex-col items-center gap-2 p-5 rounded-xl border-2 border-dashed transition-all hover:border-[var(--color-primary)] hover:bg-[var(--color-primary)]/5"
            style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-card)' }}
          >
            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(232,93,4,0.12)' }}>
              <Icon name="UserPlus" size={20} color="var(--color-primary)" />
            </div>
            <span className="font-caption text-sm font-semibold" style={{ color: 'var(--color-foreground)' }}>Join League</span>
          </button>
        </div>

        {/* Fetch error banner */}
        {leaguesError && (
          <div className="flex items-start gap-2 px-3 py-2 rounded-lg mb-3 bg-red-50 dark:bg-red-950/20 border border-red-200">
            <Icon name="AlertCircle" size={14} color="#DC2626" />
            <span className="font-caption text-xs text-red-600 break-words">Couldn't load leagues: {leaguesError}</span>
          </div>
        )}

        {/* Action error banner */}
        {actionError && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg mb-3 bg-red-50 dark:bg-red-950/20 border border-red-200">
            <Icon name="AlertCircle" size={14} color="#DC2626" />
            <span className="font-caption text-xs text-red-600">{actionError}</span>
          </div>
        )}

        {/* Your Leagues */}
        <div>
          <h2 className="font-heading font-semibold text-base mb-3" style={{ color: 'var(--color-foreground)' }}>
            Your Leagues
          </h2>
          {leagues.length === 0 ? (
            <div className="text-center py-10 px-6 rounded-xl border" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-border)' }}>
              <div className="w-14 h-14 mx-auto rounded-2xl flex items-center justify-center mb-3" style={{ backgroundColor: 'rgba(232,93,4,0.12)' }}>
                <Icon name="Trophy" size={28} color="var(--color-primary)" />
              </div>
              <h3 className="font-heading font-bold text-base mb-1.5" style={{ color: 'var(--color-foreground)' }}>
                Welcome to HoopStats
              </h3>
              <p className="font-caption text-sm mb-1" style={{ color: 'var(--color-muted-foreground)' }}>
                A league is your group of players — your friends, your team, your pickup crew.
              </p>
              <p className="font-caption text-sm" style={{ color: 'var(--color-muted-foreground)' }}>
                <strong>Create</strong> a league to invite players, or <strong>join</strong> an existing one with a 6-character code.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {activeLeagues.map((league) => (
                <LeagueRow
                  key={league.id}
                  league={league}
                  onSelect={() => handleSelectLeague(league.id)}
                  onArchive={() => handleArchive(league)}
                  onDelete={() => setConfirmDelete(league)}
                />
              ))}
              {activeLeagues.length === 0 && (
                <p className="font-caption text-sm py-3 text-center" style={{ color: 'var(--color-muted-foreground)' }}>
                  No active leagues. Create one or unarchive below.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Archived Leagues */}
        {archivedLeagues.length > 0 && (
          <div className="mt-8">
            <h2 className="font-heading font-semibold text-sm mb-2 flex items-center gap-2" style={{ color: 'var(--color-muted-foreground)' }}>
              <Icon name="Archive" size={14} color="currentColor" />
              Archived
            </h2>
            <div className="space-y-2">
              {archivedLeagues.map((league) => (
                <ArchivedLeagueRow
                  key={league.id}
                  league={league}
                  onUnarchive={() => handleUnarchive(league)}
                  onDelete={() => setConfirmDelete(league)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Pending Deletion */}
        {pendingDeleteLeagues.length > 0 && (
          <div className="mt-8">
            <h2 className="font-heading font-semibold text-sm mb-2 flex items-center gap-2" style={{ color: '#DC2626' }}>
              <Icon name="Trash2" size={14} color="currentColor" />
              Pending Deletion
            </h2>
            <p className="font-caption text-xs mb-3" style={{ color: 'var(--color-muted-foreground)' }}>
              These leagues will be permanently deleted after 30 days. You can restore them until then.
            </p>
            <div className="space-y-2">
              {pendingDeleteLeagues.map((league) => (
                <PendingDeleteRow
                  key={league.id}
                  league={league}
                  onRestore={() => handleRestore(league)}
                  onPurge={() => handlePurge(league)}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Create League Modal */}
      {showCreateModal && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
        >
          <div className="w-full max-w-sm rounded-2xl p-6" style={{ backgroundColor: 'var(--color-card)', boxShadow: 'var(--shadow-lg)' }}>
            {createdLeague ? (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-950/30 flex items-center justify-center">
                    <Icon name="CheckCircle" size={20} color="#059669" />
                  </div>
                  <h3 className="font-heading font-bold text-lg" style={{ color: 'var(--color-foreground)' }}>League Created!</h3>
                </div>
                <p className="font-caption text-sm mb-3" style={{ color: 'var(--color-muted-foreground)' }}>
                  Share this code with your friends so they can join:
                </p>
                <div className="flex items-center gap-2 p-3 rounded-lg mb-4" style={{ backgroundColor: 'var(--color-muted)' }}>
                  <span className="font-data text-2xl font-bold tracking-widest flex-1 text-center" style={{ color: 'var(--color-foreground)' }}>
                    {createdLeague.code}
                  </span>
                  <button
                    onClick={() => handleCopyCode(createdLeague.code)}
                    className="p-2 rounded-lg hover:opacity-80 transition-opacity"
                    style={{ backgroundColor: 'rgba(232,93,4,0.12)' }}
                  >
                    <Icon name="Copy" size={16} color="var(--color-primary)" />
                  </button>
                </div>
                <div className="space-y-2">
                  <Button variant="default" fullWidth onClick={() => { handleSelectLeague(createdLeague.id); }} iconName="ArrowRight" iconPosition="right">
                    Enter League
                  </Button>
                  <Button variant="outline" fullWidth onClick={() => { setShowCreateModal(false); setCreatedLeague(null); }}>
                    Close
                  </Button>
                </div>
              </>
            ) : (
              <>
                <h3 className="font-heading font-bold text-lg mb-1" style={{ color: 'var(--color-foreground)' }}>Create a League</h3>
                <p className="font-caption text-sm mb-4" style={{ color: 'var(--color-muted-foreground)' }}>
                  You'll be the owner of this league.
                </p>
                {error && (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg mb-3 bg-red-50 dark:bg-red-950/20 border border-red-200">
                    <Icon name="AlertCircle" size={14} color="#DC2626" />
                    <span className="font-caption text-xs text-red-600">{error}</span>
                  </div>
                )}
                <form onSubmit={handleCreate}>
                  <label className="block font-caption text-sm font-medium mb-1.5" style={{ color: 'var(--color-foreground)' }}>League Name</label>
                  <input
                    type="text"
                    value={leagueName}
                    onChange={(e) => setLeagueName(e?.target?.value)}
                    placeholder="e.g. Sunday Hoops, Family League"
                    required
                    autoCapitalize="words"
                    autoComplete="off"
                    className="w-full h-10 px-3 rounded-lg border font-body text-sm mb-4"
                    style={{ backgroundColor: 'var(--color-background)', borderColor: 'var(--color-border)', color: 'var(--color-foreground)' }}
                    autoFocus
                  />
                  <div className="flex gap-3">
                    <Button variant="outline" fullWidth onClick={() => setShowCreateModal(false)} type="button">Cancel</Button>
                    <Button variant="default" fullWidth loading={submitting} type="submit" iconName="Plus" iconPosition="left">Create</Button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {/* Join League Modal */}
      {showJoinModal && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
        >
          <div className="w-full max-w-sm rounded-2xl p-6" style={{ backgroundColor: 'var(--color-card)', boxShadow: 'var(--shadow-lg)' }}>
            <h3 className="font-heading font-bold text-lg mb-1" style={{ color: 'var(--color-foreground)' }}>Join a League</h3>
            <p className="font-caption text-sm mb-4" style={{ color: 'var(--color-muted-foreground)' }}>
              Enter the 6-character code from your league admin.
            </p>
            {error && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg mb-3 bg-red-50 dark:bg-red-950/20 border border-red-200">
                <Icon name="AlertCircle" size={14} color="#DC2626" />
                <span className="font-caption text-xs text-red-600">{error}</span>
              </div>
            )}
            <form onSubmit={handleJoin}>
              <label className="block font-caption text-sm font-medium mb-1.5" style={{ color: 'var(--color-foreground)' }}>League Code</label>
              <input
                type="text"
                value={joinCode}
                onChange={(e) => setJoinCode(e?.target?.value?.toUpperCase())}
                placeholder="e.g. ABC123"
                required
                maxLength={6}
                autoCapitalize="characters"
                autoComplete="off"
                autoCorrect="off"
                spellCheck="false"
                className="w-full h-10 px-3 rounded-lg border font-data text-lg text-center tracking-widest mb-4"
                style={{ backgroundColor: 'var(--color-background)', borderColor: 'var(--color-border)', color: 'var(--color-foreground)' }}
                autoFocus
              />
              <div className="flex gap-3">
                <Button variant="outline" fullWidth onClick={() => setShowJoinModal(false)} type="button">Cancel</Button>
                <Button variant="default" fullWidth loading={submitting} type="submit" iconName="UserPlus" iconPosition="left">Join</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete League Confirmation Modal */}
      {confirmDelete && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
          <div className="w-full max-w-sm rounded-2xl p-6" style={{ backgroundColor: 'var(--color-card)', boxShadow: 'var(--shadow-lg)' }}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-950/30 flex items-center justify-center">
                <Icon name="Trash2" size={20} color="#DC2626" />
              </div>
              <h3 className="font-heading font-bold text-lg" style={{ color: 'var(--color-foreground)' }}>Delete League</h3>
            </div>
            <p className="font-caption text-sm mb-3" style={{ color: 'var(--color-muted-foreground)' }}>
              <strong style={{ color: 'var(--color-foreground)' }}>{confirmDelete.name}</strong> will be marked for deletion. You have <strong>30 days</strong> to restore it before all players, games, stats, and messages are permanently removed.
            </p>
            <p className="font-caption text-sm mb-2" style={{ color: 'var(--color-foreground)' }}>
              Type the league name to confirm:
            </p>
            <input
              type="text"
              value={deleteNameInput}
              onChange={(e) => setDeleteNameInput(e.target.value)}
              placeholder={confirmDelete.name}
              autoComplete="off"
              autoCapitalize="off"
              className="w-full h-10 px-3 rounded-lg border font-body text-sm mb-4"
              style={{ backgroundColor: 'var(--color-background)', borderColor: 'var(--color-border)', color: 'var(--color-foreground)' }}
            />
            {actionError && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg mb-3 bg-red-50 dark:bg-red-950/20 border border-red-200">
                <Icon name="AlertCircle" size={14} color="#DC2626" />
                <span className="font-caption text-xs text-red-600">{actionError}</span>
              </div>
            )}
            <div className="flex gap-3">
              <button
                onClick={() => { setConfirmDelete(null); setDeleteNameInput(''); setActionError(null); }}
                disabled={deleting}
                className="flex-1 h-10 rounded-lg border font-caption text-sm font-medium"
                style={{ borderColor: 'var(--color-border)', color: 'var(--color-foreground)' }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={deleting || deleteNameInput.trim() !== confirmDelete.name}
                className="flex-1 h-10 rounded-lg font-caption text-sm font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {deleting ? 'Deleting...' : 'Delete league'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ROLE_BADGE = {
  owner: 'bg-amber-100 text-amber-700 dark:bg-amber-950/30',
  admin: 'bg-orange-100 text-orange-700 dark:bg-orange-950/30',
  member: 'bg-blue-100 text-blue-700 dark:bg-blue-950/30',
};
const ROLE_LABEL = { owner: 'Owner', admin: 'Admin', member: 'Member' };

const LeagueRow = ({ league, onSelect, onArchive, onDelete }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const isOwner = league.role === 'owner';

  return (
    <div
      className="w-full flex items-center gap-3 p-4 rounded-xl border transition-all hover:border-[var(--color-primary)] cursor-pointer"
      style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-border)' }}
      onClick={onSelect}
    >
      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(232,93,4,0.12)' }}>
        <Icon name="Trophy" size={20} color="var(--color-primary)" />
      </div>
      <div className="flex-1 text-left min-w-0">
        <div className="font-heading font-semibold text-sm truncate" style={{ color: 'var(--color-foreground)' }}>
          {league.name}
        </div>
        <div className="font-caption text-xs" style={{ color: 'var(--color-muted-foreground)' }}>
          Code: {league.code}
        </div>
      </div>
      <span className={`px-2 py-0.5 rounded-full font-caption text-xs font-semibold ${ROLE_BADGE[league.role] || ROLE_BADGE.member}`}>
        {ROLE_LABEL[league.role] || 'Member'}
      </span>
      {isOwner && (
        <div className="relative" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="p-2 rounded-lg hover:opacity-80 transition-opacity"
            style={{ backgroundColor: 'var(--color-muted)' }}
            aria-label="League actions"
          >
            <Icon name="MoreVertical" size={16} color="var(--color-muted-foreground)" />
          </button>
          {menuOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
              <div className="absolute right-0 top-full mt-1 z-20 w-44 rounded-lg border shadow-lg py-1" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-border)' }}>
                <button
                  onClick={() => { setMenuOpen(false); onArchive(); }}
                  className="w-full flex items-center gap-2 px-3 py-2 font-caption text-sm text-left hover:opacity-80"
                  style={{ color: 'var(--color-foreground)' }}
                >
                  <Icon name="Archive" size={14} color="var(--color-muted-foreground)" />
                  Archive league
                </button>
                <button
                  onClick={() => { setMenuOpen(false); onDelete(); }}
                  className="w-full flex items-center gap-2 px-3 py-2 font-caption text-sm text-left text-red-600"
                >
                  <Icon name="Trash2" size={14} color="#DC2626" />
                  Delete league
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const ArchivedLeagueRow = ({ league, onUnarchive, onDelete }) => {
  const isOwner = league.role === 'owner';
  return (
    <div
      className="flex items-center gap-3 p-3 rounded-xl border opacity-60"
      style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-border)' }}
    >
      <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--color-muted)' }}>
        <Icon name="Archive" size={18} color="var(--color-muted-foreground)" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-heading font-semibold text-sm truncate" style={{ color: 'var(--color-foreground)' }}>
          {league.name}
        </div>
        <div className="font-caption text-xs" style={{ color: 'var(--color-muted-foreground)' }}>
          Archived
        </div>
      </div>
      {isOwner ? (
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button
            onClick={onUnarchive}
            className="px-3 py-2 min-h-[40px] rounded-lg font-caption text-xs font-medium hover:opacity-80"
            style={{ backgroundColor: 'rgba(232,93,4,0.12)', color: 'var(--color-primary)' }}
          >
            Unarchive
          </button>
          <button
            onClick={onDelete}
            className="p-2.5 rounded-lg hover:opacity-80"
            style={{ backgroundColor: 'rgba(220,38,38,0.08)' }}
            aria-label="Delete league"
            title="Delete league"
          >
            <Icon name="Trash2" size={14} color="#DC2626" />
          </button>
        </div>
      ) : (
        <span className={`px-2 py-0.5 rounded-full font-caption text-xs font-semibold ${ROLE_BADGE[league.role] || ROLE_BADGE.member}`}>
          {ROLE_LABEL[league.role] || 'Member'}
        </span>
      )}
    </div>
  );
};

const PendingDeleteRow = ({ league, onRestore, onPurge }) => {
  const deletedAt = new Date(league.deleted_at);
  const elapsedDays = Math.floor((Date.now() - deletedAt.getTime()) / (1000 * 60 * 60 * 24));
  const daysRemaining = Math.max(0, 30 - elapsedDays);

  return (
    <div
      className="flex items-center gap-3 p-3 rounded-xl border"
      style={{ backgroundColor: 'rgba(220,38,38,0.04)', borderColor: 'rgba(220,38,38,0.25)' }}
    >
      <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(220,38,38,0.1)' }}>
        <Icon name="Trash2" size={18} color="#DC2626" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-heading font-semibold text-sm truncate" style={{ color: 'var(--color-foreground)' }}>
          {league.name}
        </div>
        <div className="font-caption text-xs" style={{ color: '#DC2626' }}>
          {daysRemaining > 0 ? `Deletes in ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''}` : 'Will be purged on next visit'}
        </div>
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <button
          onClick={onRestore}
          className="px-3 py-2 min-h-[40px] rounded-lg font-caption text-xs font-medium hover:opacity-80"
          style={{ backgroundColor: 'rgba(232,93,4,0.12)', color: 'var(--color-primary)' }}
        >
          Restore
        </button>
        <button
          onClick={onPurge}
          className="p-2.5 rounded-lg hover:opacity-80"
          style={{ backgroundColor: 'rgba(220,38,38,0.12)' }}
          aria-label="Delete forever now"
          title="Delete forever now"
        >
          <Icon name="X" size={14} color="#DC2626" />
        </button>
      </div>
    </div>
  );
};

export default LeagueHub;
